import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api.js';

const modalStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modal: {
    background: 'white',
    padding: '20px',
    borderRadius: '8px',
    width: '95%',
    maxWidth: '850px',
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  titleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid #eee',
    paddingBottom: '10px',
    marginBottom: '16px',
  },
  calendarGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '0',
    border: '1px solid #ddd',
    marginBottom: '16px',
    fontSize: '0.85rem',
  },
  dayHeader: {
    padding: '6px',
    textAlign: 'center',
    background: '#f4f4f4',
    borderRight: '1px solid #ddd',
    borderBottom: '1px solid #ddd',
    fontWeight: 'bold',
  },
  dayHeaderSunday: { color: 'red' },
  dayHeaderSaturday: { color: 'red' },
  cell: {
    borderRight: '1px solid #ddd',
    borderBottom: '1px solid #ddd',
    padding: '4px',
    minHeight: '32px',
  },
  cellDate: { textAlign: 'center', fontWeight: 'bold' },
  cellDateWeekend: { textAlign: 'center', fontWeight: 'bold', color: 'red' },
  cellWork: { textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' },
  cellRest: { textAlign: 'center' },
  inputTime: { width: '100%', padding: '4px', boxSizing: 'border-box' },
  timeSelectWrap: { display: 'flex', alignItems: 'center', gap: '8px' },
  timeSelect: { padding: '6px 8px', fontSize: '0.9rem', minWidth: '64px' },
  inputNumber: { width: '80px', padding: '6px' },
  label: { display: 'inline-block', marginRight: '8px', minWidth: '140px', fontWeight: 'bold' },
  row: { marginBottom: '12px' },
  buttons: { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' },
  saveButton: { padding: '8px 16px', cursor: 'pointer', background: '#28a745', color: 'white', border: 'none', borderRadius: '4px' },
  cancelButton: { padding: '8px 16px', cursor: 'pointer', background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px' },
};


const MINUTE_OPTIONS = [0, 10, 20, 30, 40, 50];
function parseTimeHHmm(s) {
  if (!s || typeof s !== 'string') return { h: 9, m: 0 };
  const [h, m] = s.trim().split(':').map((n) => parseInt(n, 10));
  const hour = Number.isFinite(h) ? Math.min(23, Math.max(0, h)) : 9;
  const minRaw = Number.isFinite(m) ? Math.min(59, Math.max(0, m)) : 0;
  const min = MINUTE_OPTIONS.reduce((a, b) => (Math.abs(b - minRaw) < Math.abs(a - minRaw) ? b : a));
  return { h: hour, m: min };
}
function formatHHmm(h, m) {
  const mm = MINUTE_OPTIONS.includes(m) ? m : MINUTE_OPTIONS.reduce((a, b) => (Math.abs(b - m) < Math.abs(a - m) ? b : a));
  return `${String(Math.min(23, Math.max(0, h))).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

function getCalendarWeeks(year, month) {
  const first = new Date(year, month - 1, 1);
  const last = new Date(year, month, 0);
  const daysInMonth = last.getDate();
  const startPad = first.getDay();
  const pad = (n) => String(n).padStart(2, '0');
  const weeks = [];
  let week = [];
  for (let i = 0; i < startPad; i++) week.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    week.push({
      day: d,
      dateStr: `${year}-${pad(month)}-${pad(d)}`,
    });
    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
  }
  if (week.length) {
    while (week.length < 7) week.push(null);
    weeks.push(week);
  }
  return weeks;
}

const ScheduleModal = ({
  isOpen,
  onClose,
  onSaved,
  participantId,
  participantName,
  year,
  month,
  initialWorkDays = [],
  initialRestDays = [],
  initialEducationDays = [],
  initialDailyHours,
  initialStartTime,
  initialEndTime,
  initialVersion = 0,
}) => {
  const [workDays, setWorkDays] = useState([]);
  const [restDays, setRestDays] = useState([]);
  const [educationDays, setEducationDays] = useState([]);
  const [dailyHours, setDailyHours] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [version, setVersion] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const weeks = useMemo(() => (year && month ? getCalendarWeeks(year, month) : []), [year, month]);
  const dayNames = ['일', '월', '화', '수', '목', '금', '토'];

  useEffect(() => {
    if (!isOpen) return;
    const hasInitial = Array.isArray(initialWorkDays) && initialWorkDays.length > 0 || Array.isArray(initialRestDays) && initialRestDays.length > 0 || Array.isArray(initialEducationDays) && initialEducationDays.length > 0;
    if (!hasInitial && year && month) {
      const weeks = getCalendarWeeks(year, month);
      const defaultWorkDays = weeks.flat().filter(Boolean).filter((cell) => {
        const d = new Date(cell.dateStr);
        const dayOfWeek = d.getDay();
        return dayOfWeek !== 0 && dayOfWeek !== 6;
      }).map((c) => c.dateStr);
      setWorkDays(defaultWorkDays);
      setRestDays([]);
      setEducationDays([]);
    } else {
      setWorkDays(Array.isArray(initialWorkDays) ? [...initialWorkDays] : []);
      setRestDays(Array.isArray(initialRestDays) ? initialRestDays.map((r) => (typeof r === 'object' && r !== null ? { ...r } : { date: r, type: '' })) : []);
      setEducationDays(Array.isArray(initialEducationDays) ? initialEducationDays.map((r) => (typeof r === 'object' && r !== null ? { ...r } : { date: r, type: '' })) : []);
    }
    setDailyHours(initialDailyHours != null && initialDailyHours !== '' ? String(initialDailyHours) : '3');
    setStartTime(initialStartTime || '09:00');
    setEndTime(initialEndTime || '12:00');
    setVersion(initialVersion ?? 0);
    setError('');
  }, [isOpen, participantId, year, month, initialWorkDays, initialRestDays, initialEducationDays, initialDailyHours, initialStartTime, initialEndTime, initialVersion]);

  const restDaysMap = useMemo(() => {
    const m = {};
    restDays.forEach((r) => {
      if (r && r.date) m[r.date] = r.type || '';
    });
    return m;
  }, [restDays]);

  const educationDaysMap = useMemo(() => {
    const m = {};
    educationDays.forEach((r) => {
      if (r && r.date) m[r.date] = r.type || '';
    });
    return m;
  }, [educationDays]);

  const toggleWork = (dateStr) => {
    setWorkDays((prev) => {
      const next = prev.filter((d) => d !== dateStr);
      if (!next.includes(dateStr)) next.push(dateStr);
      return next.sort();
    });
    setRestDays((prev) => prev.filter((r) => r.date !== dateStr));
    setEducationDays((prev) => prev.filter((r) => r.date !== dateStr));
  };

  const toggleEducation = (dateStr, type = '') => {
    setWorkDays((prev) => {
      const next = prev.filter((d) => d !== dateStr);
      if (!next.includes(dateStr)) next.push(dateStr);
      return next.sort();
    });
    setRestDays((prev) => prev.filter((r) => r.date !== dateStr));
    setEducationDays((prev) => {
      const next = prev.filter((r) => r.date !== dateStr);
      next.push({ date: dateStr, type: type || educationDaysMap[dateStr] || '' });
      return next.sort((a, b) => a.date.localeCompare(b.date));
    });
  };

  const toggleRest = (dateStr, type = '') => {
    setRestDays((prev) => {
      const next = prev.filter((r) => r.date !== dateStr);
      next.push({ date: dateStr, type: type || restDaysMap[dateStr] || '' });
      return next.sort((a, b) => a.date.localeCompare(b.date));
    });
    setWorkDays((prev) => prev.filter((d) => d !== dateStr));
    setEducationDays((prev) => prev.filter((r) => r.date !== dateStr));
  };

  const toggleUnset = (dateStr) => {
    setWorkDays((prev) => prev.filter((d) => d !== dateStr));
    setRestDays((prev) => prev.filter((r) => r.date !== dateStr));
    setEducationDays((prev) => prev.filter((r) => r.date !== dateStr));
  };

  const setEducationType = (dateStr, type) => {
    setEducationDays((prev) => {
      const idx = prev.findIndex((r) => r.date === dateStr);
      const next = [...prev];
      if (idx >= 0) next[idx] = { ...next[idx], type };
      else next.push({ date: dateStr, type });
      return next.sort((a, b) => a.date.localeCompare(b.date));
    });
  };

  const setRestType = (dateStr, type) => {
    setRestDays((prev) => {
      const idx = prev.findIndex((r) => r.date === dateStr);
      const next = [...prev];
      if (idx >= 0) next[idx] = { ...next[idx], type };
      else next.push({ date: dateStr, type });
      return next.sort((a, b) => a.date.localeCompare(b.date));
    });
  };

  const handleSave = async () => {
    if (!participantId || year == null || month == null) return;
    setSaving(true);
    setError('');
    const payload = {
      year: Number(year),
      month: Number(month),
      workDays,
      restDays: restDays.map((r) => ({ date: r.date, type: r.type || '' })),
      educationDays: educationDays.map((r) => ({ date: r.date, type: r.type || '' })),
      dailyHours: dailyHours === '' ? null : parseFloat(dailyHours),
      startTime: startTime || null,
      endTime: endTime || null,
      version,
    };
    try {
      await api.put(`/schedule/${participantId}`, payload);
      onSaved();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || '저장 실패';
      setError(msg);
      if (err.response?.status === 409) {
        setError(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={modalStyles.titleRow}>
          <h3>근무일/휴무일 지정 {participantName ? `- ${participantName}` : ''}</h3>
          <span style={{ fontSize: '0.8rem', color: '#666' }}>Version: {version}</span>
        </div>

        {weeks.length > 0 && (
          <div style={modalStyles.calendarGrid}>
            {dayNames.map((d, di) => (
              <div
                key={d}
                style={{
                  ...modalStyles.cell,
                  ...modalStyles.dayHeader,
                  ...(di === 0 ? modalStyles.dayHeaderSunday : di === 6 ? modalStyles.dayHeaderSaturday : {}),
                }}
              >
                {d}
              </div>
            ))}
            {weeks.map((week, wi) => (
              <React.Fragment key={wi}>
                {/* 날짜 행: 근무·교육일 검정, 휴무일·비지정 빨간색 */}
                {week.map((cell, ci) => {
                  const isWork = cell && workDays.includes(cell.dateStr);
                  const educationEntry = cell && educationDays.find((r) => r.date === cell.dateStr);
                  const educationType = educationEntry?.type || '';
                  return (
                    <div
                      key={`${wi}-d-${ci}`}
                      style={{
                        ...modalStyles.cell,
                        ...(isWork ? modalStyles.cellDate : modalStyles.cellDateWeekend),
                      }}
                    >
                      {cell ? (educationType ? `${cell.day} ${educationType}` : cell.day) : '-'}
                    </div>
                  );
                })}
                {/* 근무 | 교육 | 휴무 | 비지정 행: 1줄 근무/교육, 2줄 휴무/비지정 */}
                {week.map((cell, ci) => {
                  if (!cell) return <div key={`${wi}-opt-${ci}`} style={modalStyles.cell}>-</div>;
                  const isWork = workDays.includes(cell.dateStr) && !educationDays.find((r) => r.date === cell.dateStr);
                  const educationEntry = educationDays.find((r) => r.date === cell.dateStr);
                  const isEducation = !!educationEntry;
                  const restEntry = restDays.find((r) => r.date === cell.dateStr);
                  const isRest = !!restEntry;
                  const isUnset = !isWork && !isEducation && !isRest;
                  return (
                    <div key={`${wi}-opt-${ci}`} style={{ ...modalStyles.cell, ...modalStyles.cellWork }}>
                      <div>
                        <label style={{ cursor: 'pointer', marginRight: '6px' }}>
                          <input
                            type="radio"
                            name={`day-${cell.dateStr}`}
                            checked={isWork}
                            onChange={() => toggleWork(cell.dateStr)}
                          />
                          근무
                        </label>
                        <label style={{ cursor: 'pointer', marginRight: '6px' }}>
                          <input
                            type="radio"
                            name={`day-${cell.dateStr}`}
                            checked={isEducation}
                            onChange={() => toggleEducation(cell.dateStr)}
                          />
                          교육
                        </label>
                      </div>
                      <div>
                        <label style={{ cursor: 'pointer', marginRight: '6px' }}>
                          <input
                            type="radio"
                            name={`day-${cell.dateStr}`}
                            checked={isRest}
                            onChange={() => toggleRest(cell.dateStr)}
                          />
                          휴무
                        </label>
                        <label style={{ cursor: 'pointer' }}>
                          <input
                            type="radio"
                            name={`day-${cell.dateStr}`}
                            checked={isUnset}
                            onChange={() => toggleUnset(cell.dateStr)}
                          />
                          비지정
                        </label>
                      </div>
                    </div>
                  );
                })}
                {/* 휴무일/교육 입력 행 */}
                {week.map((cell, ci) => {
                  if (!cell) return <div key={`${wi}-r-${ci}`} style={modalStyles.cell}>-</div>;
                  const restEntry = restDays.find((r) => r.date === cell.dateStr);
                  const educationEntry = educationDays.find((r) => r.date === cell.dateStr);
                  const isRest = !!restEntry;
                  const isEducation = !!educationEntry;
                  return (
                    <div key={`${wi}-r-${ci}`} style={{ ...modalStyles.cell, ...modalStyles.cellRest }}>
                      {isRest ? (
                        <input
                          type="text"
                          placeholder="휴무일 종류"
                          value={restEntry?.type || ''}
                          onChange={(e) => setRestType(cell.dateStr, e.target.value)}
                          style={{ padding: '4px', fontSize: '0.8rem', width: '80px' }}
                        />
                      ) : isEducation ? (
                        <input
                          type="text"
                          placeholder="북원교육장[09~12]"
                          value={educationEntry?.type || ''}
                          onChange={(e) => setEducationType(cell.dateStr, e.target.value)}
                          style={{ padding: '4px', fontSize: '0.8rem', width: '80px' }}
                        />
                      ) : (
                        <span style={{ color: '#999' }}> / </span>
                      )}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        )}

        <div style={modalStyles.row}>
          <span style={modalStyles.label}>1일 소정근로시간 (시간)</span>
          <input
            type="number"
            style={modalStyles.inputNumber}
            min="0"
            step="0.5"
            value={dailyHours}
            onChange={(e) => setDailyHours(e.target.value)}
          />
          <span style={{
            marginLeft: '24px',
            padding: '8px 14px',
            backgroundColor: '#e8f4fd',
            border: '1px solid #0d6efd',
            borderRadius: '6px',
            fontSize: '1rem',
            fontWeight: 'bold',
            color: '#0d6efd',
          }}>
            총 지정된 근무일수 {workDays.length}일
          </span>
        </div>
        <div style={modalStyles.row}>
          <span style={modalStyles.label}>시업시간 (24시간제)</span>
          <div style={modalStyles.timeSelectWrap}>
            <select
              style={modalStyles.timeSelect}
              value={parseTimeHHmm(startTime).h}
              onChange={(e) => setStartTime(formatHHmm(Number(e.target.value), parseTimeHHmm(startTime).m))}
            >
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i}>{String(i).padStart(2, '0')}시</option>
              ))}
            </select>
            <select
              style={modalStyles.timeSelect}
              value={parseTimeHHmm(startTime).m}
              onChange={(e) => setStartTime(formatHHmm(parseTimeHHmm(startTime).h, Number(e.target.value)))}
            >
              {MINUTE_OPTIONS.map((i) => (
                <option key={i} value={i}>{String(i).padStart(2, '0')}분</option>
              ))}
            </select>
          </div>
        </div>
        <div style={modalStyles.row}>
          <span style={modalStyles.label}>종업시간 (24시간제)</span>
          <div style={modalStyles.timeSelectWrap}>
            <select
              style={modalStyles.timeSelect}
              value={parseTimeHHmm(endTime).h}
              onChange={(e) => setEndTime(formatHHmm(Number(e.target.value), parseTimeHHmm(endTime).m))}
            >
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i}>{String(i).padStart(2, '0')}시</option>
              ))}
            </select>
            <select
              style={modalStyles.timeSelect}
              value={parseTimeHHmm(endTime).m}
              onChange={(e) => setEndTime(formatHHmm(parseTimeHHmm(endTime).h, Number(e.target.value)))}
            >
              {MINUTE_OPTIONS.map((i) => (
                <option key={i} value={i}>{String(i).padStart(2, '0')}분</option>
              ))}
            </select>
          </div>
        </div>

        {error && <p style={{ color: 'red', marginTop: '10px' }}>{error}</p>}

        <div style={modalStyles.buttons}>
          <button type="button" style={modalStyles.cancelButton} onClick={onClose}>
            취소
          </button>
          <button type="button" style={modalStyles.saveButton} onClick={handleSave} disabled={saving}>
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleModal;
